import React, { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { CardShell } from './card-shell';
import type { ToolCardProps } from './types';
import { safeGet } from './types';
import { TabbioAIIcon } from '../../common/TabbioAIIcon';
import { useUpgradeModal } from '../../../providers/UpgradeModalProvider';
import { selectAiCreditLimitBanner } from '../../../lib/ai-credit-limit-banner';
import type { EntitlementDecision } from '@tabbio/types';
import { createChatUpgradeContext } from '../../../lib/monetization-context';
import { subscriptionBlockedDescription } from '../../../lib/subscription-blocked-copy';

type Decision = Partial<EntitlementDecision>;

export const SubscriptionBlockedCard: FC<ToolCardProps> = ({ result }) => {
  const { t } = useTranslation('common');
  const upgradeModal = useUpgradeModal();

  const decision = safeGet<Decision>(result, 'decision');
  const mode = safeGet<'individual' | 'employer'>(result, 'mode') ?? 'individual';
  const planSet =
    decision?.recommendedPlanSet ?? (mode === 'employer' ? 'employer' : 'individual');
  const reasonCode = decision?.reasonCode;
  const fallbackTitle =
    reasonCode === 'workspace_ai_limit_reached'
      ? t('chat.subscriptionBlocked.workspaceTitle')
      : t('chat.subscriptionBlocked.title');
  const limitBanner = selectAiCreditLimitBanner(decision, false);
  const title = limitBanner.visible
    ? t(limitBanner.titleKey, limitBanner.values)
    : decision?.message?.trim() || fallbackTitle;
  const description = limitBanner.visible
    ? t(limitBanner.hintKey, limitBanner.values)
    : subscriptionBlockedDescription(decision, t);

  return (
    <CardShell>
      <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Header: icon + label */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <TabbioAIIcon size={14} />
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)', fontWeight: 500 }}>
            {t('chat.subscriptionBlocked.label')}
          </span>
        </div>

        {/* Title */}
        <div
          dir="auto"
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: 'var(--text-primary)',
            lineHeight: '22px',
          }}
        >
          {title}
        </div>

        {/* Description */}
        <div
          dir="auto"
          style={{
            fontSize: 14,
            color: 'var(--text-secondary)',
            lineHeight: '20px',
          }}
        >
          {description}
        </div>

        {/* Action button */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <button
            type="button"
            onClick={() =>
              upgradeModal.open(
                createChatUpgradeContext({
                  decision: decision as EntitlementDecision | undefined,
                  entryPoint: 'chat.subscription_blocked_card',
                  planSet,
                  returnTo:
                    typeof window === 'undefined'
                      ? undefined
                      : `${window.location.pathname}${window.location.search}`,
                }),
              )
            }
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 14px',
              borderRadius: 8,
              border: '1px solid var(--gem-outline-variant)',
              background: 'var(--menu-bg)',
              color: 'var(--text-primary)',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'background 150ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(0,0,0,0.04)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--menu-bg)';
            }}
          >
            <span>{t('chat.subscriptionBlocked.upgradeCta')}</span>
            <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </CardShell>
  );
};
